import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from './LanguageContext';

const DestinationCard = ({ destination, index }) => {
  const containerRef = useRef(null);
  const [hasSpace, setHasSpace] = useState(false);
  const { language, t, getLocalizedPath } = useLanguage();

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setHasSpace(entry.contentRect.width >= 379);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group bg-white rounded-[2.5rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-[#99A18C]/10"
    >
      <Link to={getLocalizedPath('destination', language, { id: destination.id })}>
        <div ref={containerRef} className="relative h-64 overflow-hidden">
          <img 
            src={destination.image} 
            alt={destination.id === 'mallorca' ? (language === 'da' ? "Handicapvenlig ferie og hotel på Mallorca" : "Accessible holiday and hotel in Mallorca") : (language === 'da' ? `Handicapvenlig ferie og hotel på ${destination.title}` : `Accessible holiday and hotel in ${destination.title}`)} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#99A18C]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          <div className="absolute top-6 left-6 flex flex-col items-start gap-2">
            <div className="bg-white/90 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
              <MapPin size={14} className="text-[#99A18C]" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#99A18C]">{destination.title}</span>
            </div>
            {destination.id === 'mallorca' && (
              <div className="bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 text-white font-black text-[11px] uppercase px-3 py-1 rounded-full shadow-lg tracking-wider flex items-center gap-1.5 border border-amber-200/50 animate-pulse">
                <Sparkles size={12} className="fill-white shrink-0" />
                {language === 'da' ? 'NYHED' : 'NEW'}
              </div>
            )}
          </div>
        </div>
        
        <div className="p-8">
          <div className="flex items-start justify-between gap-2 mb-4">
            <h3 className="text-2xl font-serif font-bold text-[#99A18C] italic group-hover:text-[#8a917e] transition-colors">
              {destination.title}
            </h3>
            {destination.isComingSoon && (
              <span className="bg-[#99A18C]/10 text-[#99A18C] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#99A18C]/20 whitespace-nowrap">
                {t('ui_coming_soon')}
              </span>
            )}
          </div>
          <p className="text-slate-600 mb-6 line-clamp-2 text-sm leading-relaxed">
            {destination.description}
          </p>
          
          <div className="space-y-3 mb-8">
            {destination.features.slice(0, 2).map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                <CheckCircle2 size={14} className="text-[#99A18C]" />
                <span>{feature}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-6 border-t border-[#99A18C]/10">
            <span className="text-xs font-bold text-[#99A18C] uppercase tracking-tighter">
              {destination.isComingSoon ? t('ui_coming_soon') : t('ui_read_more')}
            </span>
            <div className="w-10 h-10 rounded-full bg-[#99A18C]/10 flex items-center justify-center text-[#99A18C] group-hover:bg-[#99A18C] group-hover:text-white transition-all duration-300">
              <ArrowRight size={20} />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default DestinationCard;
